// src/i18n/LocalizedDateDistance.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { da, nb, sv, enUS, de } from 'date-fns/locale';
import { parseApiTimestamp } from '../utils/formatters/dateFormatter';

// Utility functions that can be used by other components
export const getDateLocale = (language) => {
  switch (language) {
    case 'da': return da;
    case 'nb': return nb;
    case 'sv': return sv;
    case 'de': return de;
    case 'en':
    default: return enUS;
  }
};

export const getTimezone = (language) => {
  switch (language) {
    case 'da': return 'Europe/Copenhagen';
    case 'nb': return 'Europe/Oslo';
    case 'sv': return 'Europe/Stockholm';
    default: return 'Europe/Copenhagen';
  }
};

/**
 * Parse a UTC date string and convert it to the appropriate timezone
 * @param {string} dateString - UTC date string (without 'Z' suffix)
 * @param {string} language - Language code to determine timezone
 * @returns {Date} - Date object converted to local timezone
 */
export const parseUTCToLocalTimezone = (dateString, language) => {
  // Zone-less API timestamps are UTC; don't double-append a zone to ones that have it
  const utcDate = parseApiTimestamp(dateString);
  const timezone = getTimezone(language);
  return toZonedTime(utcDate, timezone);
};

export default function LocalizedDateDistance({ date, addSuffix = true }) {
  const { i18n } = useTranslation();

  if (date === undefined) {
    return null;
  }
  
  // Parse UTC date and convert to local timezone
  const zonedDate = parseUTCToLocalTimezone(date, i18n.language);

  return formatDistanceToNow(zonedDate, {
    addSuffix,
    locale: getDateLocale(i18n.language)
  });
}